/**
 * Global Currency Store
 * Manages currency selection and exchange rates across the entire application
 */

import { COMMON_CURRENCIES, DEFAULT_CURRENCY, getCurrencyByCode } from '@/composables/useCurrency';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

export const useCurrencyStore = defineStore('currency', () => {
  // State
  const selectedCurrency = ref(DEFAULT_CURRENCY);
  const exchangeRates = ref({});
  const isLoading = ref(false);
  const error = ref(null);
  const lastUpdated = ref(null);

  // Configuration
  const cacheTimeout = 30 * 60 * 1000; // 30 minutes
  const cacheKey = 'sanaapesa_currency_cache';

  // Computed
  const availableCurrencies = computed(() => {
    return COMMON_CURRENCIES.map(currency => {
      const rate = exchangeRates.value[currency.code.toLowerCase()] || currency.rate;
      return { ...currency, rate };
    });
  });

  const isRatesStale = computed(() => {
    if (!lastUpdated.value) return true;
    const now = new Date();
    const updated = new Date(lastUpdated.value);
    return (now - updated) > cacheTimeout;
  });

  // Actions
  const fetchExchangeRates = async (baseCurrency = 'kes') => {
    isLoading.value = true;
    error.value = null;

    try {
      const base = baseCurrency.toLowerCase();

      // Primary API URL using the new exchange API
      const primaryUrl = `https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/${base}.min.json`;
      const fallbackUrl = `https://latest.currency-api.pages.dev/v1/currencies/${base}.min.json`;

      let response;

      try {
        response = await fetch(primaryUrl);
        if (!response.ok) throw new Error('Primary API failed');
      } catch (primaryError) {
        console.warn('Primary currency API failed, trying fallback:', primaryError);
        response = await fetch(fallbackUrl);
        if (!response.ok) throw new Error('Fallback API also failed');
      }

      const data = await response.json();

      if (data[base]) {
        exchangeRates.value = data[base];
        lastUpdated.value = new Date().toISOString();

        // Save to localStorage for caching
        const cacheData = {
          rates: exchangeRates.value,
          timestamp: lastUpdated.value,
          baseCurrency: base,
          selectedCurrency: selectedCurrency.value
        };
        localStorage.setItem(cacheKey, JSON.stringify(cacheData));

        console.log(`Exchange rates updated for base currency: ${base.toUpperCase()}`);
      }

      error.value = null;
    } catch (err) {
      console.error('Failed to fetch exchange rates:', err);
      error.value = 'Failed to load current exchange rates';

      // Try to load from cache as fallback
      loadFromCache();
    } finally {
      isLoading.value = false;
    }
  };

  const loadFromCache = () => {
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const { rates, timestamp, selectedCurrency: cachedCurrency } = JSON.parse(cached);

        // Load rates regardless of age for offline functionality
        exchangeRates.value = rates || {};
        lastUpdated.value = timestamp;

        // Restore selected currency if available
        if (cachedCurrency) {
          selectedCurrency.value = cachedCurrency;
        }

        console.log('Loaded currency data from cache');
      }
    } catch (err) {
      console.warn('Failed to load from cache:', err);
    }
  };

  const selectCurrency = async (currency) => {
    const newCurrency = typeof currency === 'string' ? getCurrencyByCode(currency) : currency;

    if (!newCurrency) {
      console.warn('Invalid currency provided:', currency);
      return;
    }

    console.log(`Selecting currency: ${newCurrency.code} - ${newCurrency.name}`);

    // Force reactivity by creating a new object reference
    selectedCurrency.value = { ...newCurrency };

    // Update cache with new selection
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const cacheData = JSON.parse(cached);
        cacheData.selectedCurrency = newCurrency;
        localStorage.setItem(cacheKey, JSON.stringify(cacheData));
      } else {
        // Create new cache entry if none exists
        const cacheData = {
          rates: exchangeRates.value,
          timestamp: lastUpdated.value,
          baseCurrency: 'kes',
          selectedCurrency: newCurrency
        };
        localStorage.setItem(cacheKey, JSON.stringify(cacheData));
      }
    } catch (err) {
      console.warn('Failed to update currency cache:', err);
    }

    // If we don't have rates for this currency, fetch them
    if (newCurrency.code !== 'KES' && !exchangeRates.value[newCurrency.code.toLowerCase()]) {
      await fetchExchangeRates('kes'); // Always use KES as base for consistency
    }

    console.log(`Currency successfully changed to: ${newCurrency.code} - ${newCurrency.name}`);
    console.log('Current selectedCurrency value:', selectedCurrency.value);
  };

  const convertAmount = (amount, fromCurrency = null, toCurrency = null) => {
    if (!amount || amount === 0) return 0;

    const numericAmount = parseFloat(amount) || 0;
    const from = fromCurrency || DEFAULT_CURRENCY;
    const to = toCurrency || selectedCurrency.value;

    // If same currency, no conversion needed
    if (from.code === to.code) {
      return numericAmount;
    }

    // Get rates (KES is always base currency with rate 1)
    const fromRate = from.code === 'KES' ? 1 : (exchangeRates.value[from.code.toLowerCase()] || 1);
    const toRate = to.code === 'KES' ? 1 : (exchangeRates.value[to.code.toLowerCase()] || 1);

    // Convert: amount / fromRate * toRate
    const baseAmount = fromRate === 1 ? numericAmount : numericAmount / fromRate;
    const convertedAmount = toRate === 1 ? baseAmount : baseAmount * toRate;

    return parseFloat(convertedAmount.toFixed(2));
  };

  const formatAmount = (amount, currency = null, options = {}) => {
    // Guard against null passed explicitly (some callers pass null)
    options = options || {};
    const {
      showSymbol = true,
      decimals = 0,
      locale = 'en-KE'
    } = options;

    const targetCurrency = currency || selectedCurrency.value;
    const numericAmount = parseFloat(amount) || 0;

    if (!showSymbol) {
      return numericAmount.toLocaleString(locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      });
    }

    // Format with currency symbol
    const formattedAmount = Math.abs(numericAmount).toLocaleString(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });

    const sign = numericAmount < 0 ? '-' : '';

    // Handle different symbol positions based on currency
    switch (targetCurrency.code) {
      case 'EUR':
        return `${sign}${formattedAmount}€`;
      case 'GBP':
        return `${sign}£${formattedAmount}`;
      case 'USD':
        return `${sign}$${formattedAmount}`;
      case 'KES':
      case 'UGX':
      case 'TZS':
      default:
        return `${sign}${targetCurrency.symbol}${formattedAmount}`;
    }
  };

  const refreshRates = () => {
    return fetchExchangeRates('kes');
  };

  const initializeCurrency = async () => {
    // Load from cache first for immediate data
    loadFromCache();

    // Then fetch fresh data if cache is stale or empty
    if (isRatesStale.value || Object.keys(exchangeRates.value).length === 0) {
      await fetchExchangeRates('kes');
    }
  };

  return {
    // State
    selectedCurrency,
    exchangeRates,
    isLoading,
    error,
    lastUpdated,

    // Computed
    availableCurrencies,
    isRatesStale,

    // Actions
    selectCurrency,
    convertAmount,
    formatAmount,
    fetchExchangeRates,
    refreshRates,
    loadFromCache,
    initializeCurrency,

    // Constants
    COMMON_CURRENCIES
  };
});
